/**
 * One program's embed-facing data, keyed in the lookup by its config id (which
 * equals `BonusProgram.id` and the reward output's `bonusConfigDocId`): its
 * display name plus its non-archived periods.
 */
export interface EmbedProgram {
  name: string;
  periods: BonusTypes.BonusPeriod[];
  // The program's output dataset (BonusProgram.dataset) — used to resolve the
  // raw bonus record for the DRIVER_TYPE handshake.
  dataset: string;
}

/**
 * Builds the per-program lookup the embed handshake answers from, keyed by
 * program config id. Powers both handshakes:
 *   - PERIODS_REQUEST — one program's periods for a year.
 *   - PROGRAMS_REQUEST — every program (name + ongoing period), so the embed
 *     can render a home card per program (including an empty-state card for a
 *     program with no ongoing period).
 *
 * Every account program gets an entry (even one with no periods), so the embed
 * can always show a card for it. Archived periods are excluded — they aren't
 * available in the driver app.
 */
const getEmbedProgramsByConfigId = ({
  bonusPrograms,
  bonusPeriodsLookup,
}: {
  bonusPrograms: BonusTypes.BonusProgram[];
  bonusPeriodsLookup: {
    [program: string]: BonusTypes.BonusPeriod[] | undefined;
  };
}): { [bonusConfigDocId: string]: EmbedProgram } => {
  const lookup: { [bonusConfigDocId: string]: EmbedProgram } = {};
  bonusPrograms.forEach((program) => {
    lookup[program.id] = {
      name: program.name,
      periods: (bonusPeriodsLookup[program.id] ?? []).filter(
        (period) => period.status !== 'archived',
      ),
      dataset: program.dataset,
    };
  });
  return lookup;
};

export default getEmbedProgramsByConfigId;
